import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { Loader2, Play, Check, X, AlertTriangle, Link2 } from 'lucide-react'

/**
 * Benchmark mapping — which market does each strategy actually trade?
 *
 * A strategy's own record rarely holds enough extreme days to fit a tail to. The market
 * it trades has been observed since 2000, with 2008 and 2020 inside the sample, so the
 * tail is estimated there and translated back through the strategy's response.
 *
 * The page is built around a refusal. A mapping is a claim about the world, so the
 * engine only ever *proposes*; nothing drives a risk number until a person confirms it,
 * and the evidence behind each proposal is on screen next to the button that accepts it.
 */

const th = 'px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap'
const td = 'px-3 py-2 text-sm text-gray-800 whitespace-nowrap'
const inp = 'rounded-md border border-gray-300 px-2 py-1 text-sm'
const btn = 'flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs hover:bg-gray-50 disabled:opacity-50'
const btnPrimary = 'flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50'

const num = (v: any, d = 2) => (v == null || Number.isNaN(v) ? '—' : Number(v).toFixed(d))

const BASIS_STYLE: Record<string, string> = {
  confirmed: 'bg-emerald-100 text-emerald-800',
  manual: 'bg-blue-100 text-blue-800',
  suggested: 'bg-gray-100 text-gray-600',
  rejected: 'bg-amber-100 text-amber-800',
}

export default function BenchmarkMapping() {
  const qc = useQueryClient()
  const [limit, setLimit] = useState(40)
  const [status, setStatus] = useState('')

  const universe = useQuery({
    queryKey: ['benchmark-universe'],
    queryFn: () => api.get('/strategy-lab/benchmarks').then(r => r.data),
  })

  const mappings = useQuery({
    queryKey: ['benchmark-mappings'],
    queryFn: () => api.get('/strategy-lab/benchmarks/mappings').then(r => r.data),
  })

  const suggest = useMutation({
    mutationFn: async () => {
      const sel = await api.get('/strategy-lab/select', {
        params: { limit_tickers: 400, top_n: limit },
      }).then(r => r.data)
      const tickers = (sel.rows || []).map((r: any) => r.name)
      return api.post('/strategy-lab/benchmarks/suggest', { tickers, top_n: 3 })
        .then(r => r.data)
    },
    onSuccess: (d) => setStatus(d.available ? `Scored ${d.rows?.length ?? 0} strategies against ${d.n_instruments} benchmarks` : d.reason),
    onError: (e: any) => setStatus(e?.response?.data?.detail || 'Scoring failed'),
  })

  const save = useMutation({
    mutationFn: (body: any) => api.post('/strategy-lab/benchmarks/mappings', body).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['benchmark-mappings'] })
      setStatus('Mapping saved')
    },
    onError: (e: any) => setStatus(e?.response?.data?.detail || 'Save failed'),
  })

  const instruments: any[] = universe.data?.instruments || []
  const rows: any[] = suggest.data?.rows || []
  const stored: any[] = mappings.data?.rows || []
  const storedByTicker = Object.fromEntries(stored.map(r => [r.ticker, r]))

  const confirmOne = (ticker: string, c: any, basis = 'confirmed') =>
    save.mutate({
      ticker, instrument_id: c?.instrument_id ?? null, basis,
      stats: c ? { tail_r2: c.tail_r2, tail_beta: c.tail_beta, power_b: c.power_b, history_gain: c.history_gain } : null,
      note: c ? `${c.benchmark}: tail R² ${num(c.tail_r2, 3)}, exponent ${num(c.power_b)}` : 'no benchmark',
    })

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Benchmark Mapping</h1>
          <p className="max-w-3xl text-sm text-gray-500">
            Which market each strategy actually trades, so its tail can be estimated on that
            market's far longer history instead of its own short one.
          </p>
        </div>
        {status && <div className="rounded bg-gray-100 px-3 py-1.5 text-xs text-gray-600">{status}</div>}
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Kpi label="Benchmark instruments" value={instruments.length || '—'} />
        <Kpi label="Mappings stored" value={mappings.data?.n_total ?? '—'} />
        <Kpi label="Confirmed (used for risk)" value={mappings.data?.n_confirmed ?? '—'} />
      </div>

      <Note>
        Only <strong>confirmed</strong> and <strong>manual</strong> mappings are used for risk.
        A suggestion nobody has examined must not quietly drive a number, which is why
        provenance is stored rather than a yes/no flag.
      </Note>

      <div className="my-4 flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
        <label className="text-sm">
          Strategies to score <input className={`${inp} ml-1 w-20`} type="number" value={limit}
            onChange={e => setLimit(+e.target.value)} />
        </label>
        <button className={btnPrimary} disabled={suggest.isPending} onClick={() => suggest.mutate()}>
          {suggest.isPending ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />}
          Score candidates
        </button>
      </div>

      {suggest.isPending && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="animate-spin" size={16} />
          Fitting every strategy against every benchmark's tail…
        </div>
      )}

      {suggest.data?.note && <Note>{suggest.data.note}</Note>}

      {rows.length > 0 && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-2 text-sm font-medium text-gray-700">
            Proposed mappings — ranked by how much of each strategy's tail the benchmark explains
          </div>
          <div className="max-h-[36rem] overflow-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 bg-white">
                <tr>
                  {['Strategy', 'Current', 'Best candidate', 'Tail R²', 'Full R²', 'Tail β',
                    'Exponent', 'History gained', '', ''].map((h, i) =>
                    <th key={i} className={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r: any) => {
                  const c = (r.candidates || [])[0]
                  const cur = storedByTicker[r.ticker] || r.current
                  return (
                    <tr key={r.ticker} className="hover:bg-gray-50">
                      <td className={td}>{r.ticker}</td>
                      <td className={td}>
                        {cur?.benchmark
                          ? <span className={`rounded px-2 py-0.5 text-xs ${BASIS_STYLE[cur.basis] || ''}`}>
                              {cur.benchmark} · {cur.basis}
                            </span>
                          : <span className="text-xs text-gray-400">none</span>}
                      </td>
                      <td className={td}>{c?.benchmark ?? <span className="text-xs text-gray-400">{r.reason || 'none'}</span>}</td>
                      <td className={`${td} font-medium`}>{num(c?.tail_r2, 3)}</td>
                      <td className={td}>{num(c?.full_r2, 3)}</td>
                      <td className={td}>{num(c?.tail_beta, 3)}</td>
                      <td className={td} title={c?.convex_loss ? 'above 1: losses grow faster than the market falls' : undefined}>
                        {num(c?.power_b)}{c?.convex_loss ? ' ⚠' : ''}
                      </td>
                      <td className={td}>{c ? `${num(c.history_gain, 1)} yrs` : '—'}</td>
                      <td className={td}>
                        {c && !c.usable && (
                          <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800"
                                title="Below the 20% tail-R² bar: the benchmark does not explain this strategy's losses well enough to lend it a tail.">
                            too weak
                          </span>
                        )}
                      </td>
                      <td className={td}>
                        <div className="flex gap-1">
                          <button className={btn} disabled={!c?.instrument_id || save.isPending}
                                  onClick={() => confirmOne(r.ticker, c)}>
                            <Check size={12} />Confirm
                          </button>
                          <button className={btn} disabled={save.isPending}
                                  onClick={() => confirmOne(r.ticker, null, 'rejected')}>
                            <X size={12} />None
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Tail R² above full-sample R² means the benchmark matters more in a crash than on an
            ordinary day — exactly the case a correlation-based mapping would get wrong.
            An exponent above 1 marks convex loss: short optionality.
          </div>
        </div>
      )}

      {stored.length > 0 && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-2 text-sm font-medium text-gray-700">Stored mappings</div>
          <div className="max-h-96 overflow-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 bg-white">
                <tr>{['Strategy', 'Benchmark', 'Basis', 'Tail R²', 'Exponent', 'By', 'When', ''].map(h =>
                  <th key={h} className={th}>{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stored.map((m: any) => (
                  <tr key={m.ticker} className="hover:bg-gray-50">
                    <td className={td}>{m.ticker}</td>
                    <td className={td}>{m.benchmark || '—'}</td>
                    <td className={td}>
                      <span className={`rounded px-2 py-0.5 text-xs ${BASIS_STYLE[m.basis] || ''}`}>{m.basis}</span>
                    </td>
                    <td className={td}>{num(m.tail_r2, 3)}</td>
                    <td className={td}>{num(m.power_b)}</td>
                    <td className={td}>{m.updated_by || '—'}</td>
                    <td className={td}>{m.updated_at ? String(m.updated_at).slice(0, 10) : '—'}</td>
                    <td className={td}>
                      <button className={btn} disabled={save.isPending}
                              onClick={() => confirmOne(m.ticker, null, 'rejected')}>
                        <X size={12} />Unset
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {instruments.length > 0 && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
            <Link2 size={14} />Benchmark universe
          </div>
          {universe.data?.note && <Note>{universe.data.note}</Note>}
          <div className="mt-3 max-h-80 overflow-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 bg-white">
                <tr>{['Ticker', 'Name', 'Type', 'Years', 'From', 'To'].map(h =>
                  <th key={h} className={th}>{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {instruments.map((i: any) => (
                  <tr key={i.instrument_id}>
                    <td className={td}>{i.short_name}</td>
                    <td className={td}>{i.name}</td>
                    <td className={td}>{i.asset_type}</td>
                    <td className={td}>{num(i.years, 1)}</td>
                    <td className={td}>{String(i.first_date).slice(0, 10)}</td>
                    <td className={td}>{String(i.last_date).slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function Kpi({ label, value }: { label: string; value: any }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-0.5 text-xl font-semibold text-gray-900">{value ?? '—'}</div>
    </div>
  )
}

function Note({ children }: { children: any }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
      <AlertTriangle size={14} className="mt-0.5 flex-none" />
      <div>{children}</div>
    </div>
  )
}
